import { useState, useEffect, useRef } from 'react'

export function ShortcutsHelp() {
  const [open, setOpen] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handle(e: Event) {
      if ((e as CustomEvent).detail === 'help') setOpen(true)
    }
    window.addEventListener('rize:shortcut', handle)
    return () => window.removeEventListener('rize:shortcut', handle)
  }, [])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    // Focus first focusable element when dialog opens
    if (dialogRef.current) {
      const btn = dialogRef.current.querySelector<HTMLElement>('button, [tabindex]:not([tabindex="-1"])')
      btn?.focus()
    }
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null

  const sections = [
    {
      title: 'Navigation (press g, then...)',
      shortcuts: [
        { keys: ['g', 'h'], desc: 'Dashboard' },
        { keys: ['g', 'c'], desc: 'Contacts' },
        { keys: ['g', 'l'], desc: 'Calls' },
        { keys: ['g', 't'], desc: 'Transactions' },
        { keys: ['g', 'p'], desc: 'Properties' },
        { keys: ['g', 'd'], desc: 'Documents' },
        { keys: ['g', 'f'], desc: 'Follow-Up' },
        { keys: ['g', 'i'], desc: 'Inbox' },
        { keys: ['g', 'a'], desc: 'Analytics' },
        { keys: ['g', 's'], desc: 'Settings' },
      ],
    },
    {
      title: 'Actions',
      shortcuts: [
        { keys: ['n'], desc: 'New (context-aware: contact, task, etc.)' },
        { keys: ['['], desc: 'Collapse or expand the left sidebar' },
        { keys: ['Cmd', 'K'], desc: 'Search everything' },
        { keys: ['?'], desc: 'Show this help' },
        { keys: ['Esc'], desc: 'Close dialogs' },
      ],
    },
  ]

  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label="Keyboard shortcuts">
      <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} aria-hidden="true" />
      <div ref={dialogRef} className="relative mx-auto mt-[10vh] w-full max-w-lg rounded-lg border border-border bg-card p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Keyboard Shortcuts</h2>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close keyboard shortcuts"
            className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card"
          >
            ESC
          </button>
        </div>

        <div className="space-y-6">

          {sections.map(section => (
            <div key={section.title}>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{section.title}</h3>
              <div className="space-y-1">
                {section.shortcuts.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <span className="text-sm text-muted-foreground">{s.desc}</span>
                    <div className="flex items-center gap-1" aria-label={'Shortcut: ' + s.keys.join(' then ')}>
                      {s.keys.map((key, j) => (
                        <span key={j}>
                          {j > 0 && <span className="mx-0.5 text-[10px] text-muted-foreground" aria-hidden="true">then</span>}
                          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-mono">{key}</kbd>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

